/**
 * Client-side rate limiter for IMEI check attempts.
 * Counts every submission attempt (success or failure) and applies cooldown.
 */
export const IMEI_CHECK_MAX_ATTEMPTS = 3;
export const IMEI_CHECK_COOLDOWN_MS = 5 * 60 * 1000;

const STORAGE_KEY = "vle-imei-check-attempt-state";

export type ImeiCheckAttemptState = {
  attempts: number;
  cooldownUntil: number | null;
};

function readState(): ImeiCheckAttemptState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { attempts: 0, cooldownUntil: null };
    const parsed = JSON.parse(raw) as Partial<ImeiCheckAttemptState>;
    return {
      attempts:
        typeof parsed.attempts === "number" && parsed.attempts > 0
          ? parsed.attempts
          : 0,
      cooldownUntil:
        typeof parsed.cooldownUntil === "number" ? parsed.cooldownUntil : null,
    };
  } catch {
    return { attempts: 0, cooldownUntil: null };
  }
}

function writeState(state: ImeiCheckAttemptState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // quota/private mode - degrade gracefully
  }
}

export function getImeiCheckAttemptStorageKey(): string {
  return STORAGE_KEY;
}

export function resolveImeiCheckAttemptState(): ImeiCheckAttemptState {
  const current = readState();
  if (current.cooldownUntil != null && Date.now() >= current.cooldownUntil) {
    const cleared = { attempts: 0, cooldownUntil: null };
    writeState(cleared);
    return cleared;
  }
  return current;
}

export function recordImeiCheckAttempt(): ImeiCheckAttemptState {
  const current = resolveImeiCheckAttemptState();
  const now = Date.now();
  if (current.cooldownUntil != null && now < current.cooldownUntil) {
    return current;
  }

  const attempts = current.attempts + 1;
  const cooldownUntil =
    attempts >= IMEI_CHECK_MAX_ATTEMPTS ? now + IMEI_CHECK_COOLDOWN_MS : null;
  const next: ImeiCheckAttemptState = { attempts, cooldownUntil };
  writeState(next);
  return next;
}

export function remainingCooldownSeconds(
  cooldownUntil: number | null,
  nowMs: number = Date.now(),
): number {
  if (cooldownUntil == null || nowMs >= cooldownUntil) return 0;
  return Math.ceil((cooldownUntil - nowMs) / 1000);
}

export function formatCooldownMinutesSeconds(totalSeconds: number): string {
  const raw = Math.max(0, Math.floor(totalSeconds));
  const displayTotal = raw > 1 ? raw - 1 : raw;
  const minutes = Math.floor(displayTotal / 60);
  const seconds = displayTotal % 60;
  const minuteWord = minutes === 1 ? "minute" : "minutes";
  const secondWord = seconds === 1 ? "second" : "seconds";

  if (minutes === 0) return `${seconds} ${secondWord}`;
  if (seconds === 0) return `${minutes} ${minuteWord}`;
  return `${minutes} ${minuteWord} ${seconds} ${secondWord}`;
}
